export const AGENT_ACTIVITY_KEY = "activitySteps";

export type AgentActivityStatus = "running" | "done";

export interface AgentActivity {
  id: string;
  title: string;
  detail: string;
  status: AgentActivityStatus;
}

/**
 * The only wire fields this module reads, typed `unknown` because the stream is untyped data
 * from the agent. Everything else on these events (tool args, results) can carry PHI and must
 * not reach the UI — leaving it out of this interface keeps that true by construction.
 *
 * `text` and `message_id` are read only from `subagent_progress`, the one place sub-agent prose
 * is shown here. That text can name a patient. It becomes a step row in the message's in-memory
 * metadata only: nothing writes it to storage, and the history loader rebuilds messages from the
 * server without steps, so it is gone on reload.
 */
export interface ActivityEventData {
  sub_agent?: unknown;
  invocation_id?: unknown;
  tool_call_id?: unknown;
  id?: unknown;
  name?: unknown;
  text?: unknown;
  message_id?: unknown;
  step_id?: unknown;
  state?: unknown;
}

export const NO_ACTIVITY: readonly AgentActivity[] = [];

const SUB_AGENT_TITLES: Record<string, string> = {
  clinical_workflow_chat: "Clinical Workflows",
  ehr: "Patient Records",
  messaging: "Messaging",
  scheduling: "On-Call Lookup",
};

const SUB_AGENT_DETAILS: Record<string, string> = {
  clinical_workflow_chat: "Configuring the workflow…",
  customer_support: "Fetching relevant support details…",
  ehr: "Retrieving chart data from the EHR…",
  messaging: "Working in your conversations…",
  scheduling: "Querying the on-call schedule…",
};

const TOOL_TITLES: Record<string, string> = {
  forward_message: "Preparing the answer",
  get_user_identity: "Checking your identity",
};

const TOOL_DETAILS: Record<string, string> = {
  forward_message: "Formatting the response…",
  get_user_identity: "Verifying your access…",
};

function humanize(name: string): string {
  return name
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function wireString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Sub-agent progress, one step row per item under the invocation (ids `<invocation_id>:…`):
 *
 * - narration (`message_id`): a sentence the model wrote before a tool call. A new narration
 *   message finishes the previous one; more text for the same message extends it.
 * - a tool step (`step_id` + `state`): the server's sentences for one tool call. `running` adds
 *   a row ("Resolving the patient's FHIR ID…"); `done`/`error` settles that row and adds the
 *   result as a row of its own after it ("Patient found in the EHR"), so the list reads as the
 *   steps happened.
 */
function applyProgress(
  activities: readonly AgentActivity[],
  invocationId: string | undefined,
  text: string | undefined,
  messageId: string | undefined,
  stepId: string | undefined,
  state: string | undefined,
): readonly AgentActivity[] {
  if (!invocationId || !text) return activities;
  const parent = activities.find((activity) => activity.id === invocationId);
  if (!parent || parent.status === "done") return activities;

  if (stepId) {
    const startId = `${invocationId}:s:${stepId}`;
    const resultId = `${invocationId}:r:${stepId}`;
    if (state !== "done" && state !== "error") {
      if (activities.some((activity) => activity.id === startId)) return activities;
      // Narration comes before the tool it leads into, so by the time a step starts that
      // narration is over. Left running it would spin until the invocation ended, and become
      // the summary line (the newest running step) as soon as the tool finished.
      const narrationPrefix = `${invocationId}:m:`;
      const settled = activities.map((activity) =>
        activity.id.startsWith(narrationPrefix) && activity.status === "running"
          ? { ...activity, status: "done" as const }
          : activity,
      );
      return [...settled, { id: startId, title: text, detail: "", status: "running" }];
    }
    if (activities.some((activity) => activity.id === resultId)) return activities;
    // The start row stays as the record of what began; the result is its own row after it.
    return [
      ...activities.map((activity) =>
        activity.id === startId && activity.status === "running"
          ? { ...activity, status: "done" as const }
          : activity,
      ),
      { id: resultId, title: text, detail: "", status: "done" },
    ];
  }

  const narrationPrefix = `${invocationId}:m:`;
  const rowId = `${narrationPrefix}${messageId ?? ""}`;
  const existing = activities.find((activity) => activity.id === rowId);
  if (existing) {
    if (existing.status === "done") return activities;
    return activities.map((activity) =>
      activity.id === rowId ? { ...activity, title: activity.title + text } : activity,
    );
  }
  const settled = activities.map((activity) =>
    activity.id.startsWith(narrationPrefix) && activity.status === "running"
      ? { ...activity, status: "done" as const }
      : activity,
  );
  return [...settled, { id: rowId, title: text, detail: "", status: "running" }];
}

function finishActivity(
  activities: readonly AgentActivity[],
  id: string | undefined,
): readonly AgentActivity[] {
  if (!id) return activities;
  let changed = false;
  const next = activities.map((activity) => {
    if ((activity.id !== id && !activity.id.startsWith(`${id}:`)) || activity.status === "done")
      return activity;
    changed = true;
    return { ...activity, status: "done" as const };
  });
  return changed ? next : activities;
}

function finishRunning(activities: readonly AgentActivity[]): readonly AgentActivity[] {
  if (!activities.some((activity) => activity.status === "running")) return activities;
  return activities.map((activity) =>
    activity.status === "running" ? { ...activity, status: "done" as const } : activity,
  );
}

export function applyActivityEvent(
  activities: readonly AgentActivity[],
  event: string | undefined,
  data: ActivityEventData = {},
): readonly AgentActivity[] {
  switch (event) {
    case "subagent_invocation_start": {
      const id = wireString(data.invocation_id);
      const name = wireString(data.sub_agent);
      if (!id || !name || activities.some((activity) => activity.id === id)) return activities;
      return [
        ...activities,
        {
          id,
          title: SUB_AGENT_TITLES[name] ?? humanize(name),
          detail: SUB_AGENT_DETAILS[name] ?? "",
          status: "running",
        },
      ];
    }

    case "subagent_progress":
      return applyProgress(
        activities,
        wireString(data.invocation_id),
        wireString(data.text),
        wireString(data.message_id),
        wireString(data.step_id),
        wireString(data.state),
      );

    case "subagent_invocation_end":
      return finishActivity(activities, wireString(data.invocation_id));

    case "tool_call": {
      const id = wireString(data.id);
      const name = wireString(data.name);
      // A sub-agent invocation also arrives as a `tool_call` named after the sub-agent, but
      // only after that sub-agent has finished (the blocking tool step is what holds the
      // stream). Its invocation events already own the row, so drop this one rather than
      // flash a duplicate at the end of the turn.
      if (!id || !name || name in SUB_AGENT_TITLES) return activities;
      const title = TOOL_TITLES[name];
      if (!title || activities.some((activity) => activity.id === id)) return activities;
      return [...activities, { id, title, detail: TOOL_DETAILS[name] ?? "", status: "running" }];
    }

    case "tool_result":
      return finishActivity(activities, wireString(data.tool_call_id));

    case "done":
    case "error":
      return finishRunning(activities);

    default:
      return activities;
  }
}
